/**
 * Keyboard Shortcuts Help Modal
 *
 * Provides comprehensive documentation of all keyboard shortcuts available in the app.
 * Organized by category for easier navigation.
 * Accessible via keyboard shortcut button or help menu.
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Icon } from '@iconify/react'
import {
  formatShortcutKeys,
  getAllCategories,
  getShortcutsByCategory,
  type KeyboardShortcut,
} from '@/config/keyboardShortcuts'

interface KeyboardShortcutsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Display a single keyboard shortcut key
 */
function KeyboardKey({ keyName }: { keyName: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-background dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-sm">
      {keyName}
    </kbd>
  )
}

/**
 * Display a keyboard shortcut entry
 */
function ShortcutRow({ shortcut }: { shortcut: KeyboardShortcut }) {
  const keys = formatShortcutKeys(shortcut)

  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <div className="flex-1">
        <p className="text-fluid-sm font-medium text-foreground">{shortcut.name}</p>
        <p className="text-fluid-xs text-muted-foreground mt-0.5">{shortcut.description}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <KeyboardKey keyName={keys} />
      </div>
    </div>
  )
}

function CategorySection({ category }: { category: KeyboardShortcut['category'] }) {
  const shortcuts = getShortcutsByCategory(category)

  if (shortcuts.length === 0) return null

  // Category icons
  const categoryIcons: Record<KeyboardShortcut['category'], string> = {
    TTS: 'radix-icons:speaker-loud',
    Navigation: 'radix-icons:dashboard',
    Accessibility: 'radix-icons:accessibility',
    General: 'radix-icons:gear',
  }

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon icon={categoryIcons[category]} className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold">{category}</h3>
      </div>
      <div className="rounded-lg border border-border/50 p-3">
        {shortcuts.map((shortcut) => (
          <ShortcutRow key={shortcut.id} shortcut={shortcut} />
        ))}
      </div>
    </div>
  )
}

export function KeyboardShortcutsModal({ open, onOpenChange }: KeyboardShortcutsModalProps) {
  const categories = getAllCategories()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Icon icon="radix-icons:keyboard" className="w-5 h-5" />
            Keyboard Shortcuts
          </DialogTitle>
          <DialogDescription>
            Use these keyboard shortcuts to navigate and control the app more efficiently.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {categories.map((category) => (
            <CategorySection key={category} category={category} />
          ))}
        </div>

        <div className="mt-4 pt-4 border-t text-xs text-muted-foreground">
          <p>
            <strong>Tip:</strong> Keyboard shortcuts won't work when typing in text fields. Press{' '}
            <KeyboardKey keyName="Escape" /> to close this modal.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
